import React, { useState, useRef, useEffect } from 'react';
import moment from 'moment';

export default function MobileNavigation({
  currentView,
  setCurrentView,
  currentDate,
  handleNavigate,
  onAddNew,
  onRefresh,
  isRefreshing,
}) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showMobileDropdown, setShowMobileDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const mobileDropdownRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
      if (mobileDropdownRef.current && !mobileDropdownRef.current.contains(event.target)) {
        setShowMobileDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <>
      {/* Mobile Header - Fixed at top */}
      <div className="md:hidden fixed top-0 left-0 right-0 flex items-center justify-between p-3 border-b border-gray-200 bg-white z-[2000]">
        {/* Left: Hamburger Menu */}
        <button
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          className="p-2 rounded-lg hover:bg-gray-100 transition-colors min-h-touch-target min-w-[60px] flex items-center justify-center"
          aria-label="Menu"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            {isMenuOpen ? (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            )}
          </svg>
        </button>

        {/* Center: Previous + Date + Next */}
        <div className="flex items-center gap-2 flex-1 justify-center">
          <button
            onClick={() => {
              const unit = currentView === 'day' || currentView === 'three_day' ? 'day' : currentView === 'month' ? 'month' : 'week';
              const amount = 1;
              const newDate = moment(currentDate).subtract(amount, unit).toDate();
              handleNavigate(newDate);
            }}
            className="p-2 rounded-full hover:bg-gray-100 transition-colors min-h-touch-target min-w-touch-target flex items-center justify-center"
            aria-label="Previous"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          <div className="flex flex-col items-center mx-2">
            {(currentView === 'week' || currentView === 'work_week') && (
              <span className="text-xs font-medium px-2 py-0.5 bg-[#205799] text-white rounded-full mb-1">
                W{moment(currentDate).format('w')}
              </span>
            )}
            {currentView === 'three_day' && (
              <span className="text-xs font-medium px-2 py-0.5 bg-[#205799] text-white rounded-full mb-1">
                3D
              </span>
            )}
            <h2 className="text-base font-semibold text-center">
              {currentView === 'day' 
                ? moment(currentDate).format('MMM D')
                : currentView === 'three_day'
                ? (() => {
                    const start = moment(currentDate);
                    const end = moment(currentDate).add(2, 'days');
                    return start.month() === end.month() 
                      ? `${start.format('MMM D')}-${end.format('D')}`
                      : `${start.format('MMM D')} - ${end.format('MMM D')}`;
                  })()
                : moment(currentDate).format('MMM YYYY')}
            </h2>
          </div>

          <button
            onClick={() => {
              const unit = currentView === 'day' || currentView === 'three_day' ? 'day' : currentView === 'month' ? 'month' : 'week';
              const amount = 1;
              const newDate = moment(currentDate).add(amount, unit).toDate();
              handleNavigate(newDate);
            }}
            className="p-2 rounded-full hover:bg-gray-100 transition-colors min-h-touch-target min-w-touch-target flex items-center justify-center"
            aria-label="Next"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>

        {/* Right: Add New Dropdown */}
        <div className="relative" ref={mobileDropdownRef}>
          <button
            onClick={() => setShowMobileDropdown(!showMobileDropdown)}
            className="px-5 py-2 text-xs font-bold bg-[#205799] text-white rounded-lg min-h-touch-target min-w-[60px] flex items-center gap-1"
          >
            Add
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          
          {/* Mobile Dropdown Menu */}
          {showMobileDropdown && (
            <div className="absolute right-0 mt-2 w-44 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
              <div className="py-1">
                <button
                  onClick={() => {
                    onAddNew();
                    setShowMobileDropdown(false);
                  }}
                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  New entry
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {isMenuOpen && (
        <div className="md:hidden fixed inset-0 z-[2100] bg-black bg-opacity-50 hamburger-menu-overlay" onClick={() => setIsMenuOpen(false)}>
          <div className="bg-white w-80 h-full shadow-xl" onClick={(e) => e.stopPropagation()}>
            {/* Menu Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold">Din Forsikringsret</h3>
              <button
                onClick={() => setIsMenuOpen(false)}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Menu Content */}
            <div className="p-4 space-y-6">
              {/* Date Navigation */}
              <div>
                <h4 className="text-sm font-bold text-gray-700 mb-3">Date Navigation</h4>
                <button
                  onClick={() => {
                    handleNavigate(new Date());
                    setIsMenuOpen(false);
                  }}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors font-medium text-left"
                >
                  Today
                </button>
              </div>

              {/* View Selection */}
              <div>
                <h4 className="text-sm font-bold text-gray-700 mb-3">Select view</h4>
                <div className="space-y-2">
                  {[
                    { key: 'month', label: 'Month View' },
                    { key: 'week', label: 'Week View' },
                    { key: 'day', label: 'Day View' }
                  ].map((view) => (
                    <button
                      key={view.key}
                      onClick={() => {
                        setCurrentView(view.key);
                        setIsMenuOpen(false);
                      }}
                      className={`w-full px-4 py-3 rounded-lg text-left transition-colors ${
                        currentView === view.key
                          ? 'bg-white border border-[#205799] text-[#205799] font-bold'
                          : 'border border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {view.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Quick Actions */}
              <div>
                <h4 className="text-sm font-bold text-gray-700 mb-3">Add new</h4>
                <div className="space-y-2">
                  <button
                    onClick={() => {
                      onAddNew();
                      setIsMenuOpen(false);
                    }}
                    className="w-full px-4 py-3 bg-[#205799] text-white rounded-lg font-medium hover:bg-[#273C61] transition-colors"
                  >
                    New entry
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Desktop Navigation - Fixed at top */}
      <div className="hidden md:flex fixed top-0 left-0 right-0 items-center justify-between gap-4 p-4 border-b border-gray-200 bg-white z-[2000]">
        <div className="flex items-center gap-4">
          <button
            onClick={() => handleNavigate(new Date())}
            className="px-6 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors font-medium"
          >
            Today
          </button>
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                const unit = currentView === 'day' || currentView === 'three_day' ? 'day' : (currentView === 'month' || currentView === 'list') ? 'month' : 'week';
                const amount = 1;
                const newDate = moment(currentDate).subtract(amount, unit).toDate();
                handleNavigate(newDate);
              }}
              className="p-2 rounded-full hover:bg-gray-100 transition-colors"
              aria-label="Previous"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button
              onClick={() => {
                const unit = currentView === 'day' || currentView === 'three_day' ? 'day' : (currentView === 'month' || currentView === 'list') ? 'month' : 'week';
                const amount = 1;
                const newDate = moment(currentDate).add(amount, unit).toDate();
                handleNavigate(newDate);
              }}
              className="p-2 rounded-full hover:bg-gray-100 transition-colors"
              aria-label="Next"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
          <h2 className="text-xl font-semibold flex items-center gap-2">
            {currentView === 'day' 
              ? moment(currentDate).format('dddd, MMMM D, YYYY')
              : currentView === 'three_day'
              ? (() => {
                  const start = moment(currentDate);
                  const end = moment(currentDate).add(2, 'days');
                  return start.month() === end.month() 
                    ? `${start.format('MMMM D')}-${end.format('D, YYYY')}`
                    : `${start.format('MMMM D')} - ${end.format('MMMM D, YYYY')}`;
                })()
              : moment(currentDate).format('MMMM YYYY')}
            {(currentView === 'week' || currentView === 'work_week') && (
              <span className="text-sm font-normal px-2 py-0.5 bg-gray-100 rounded-full">
                Week {moment(currentDate).format('w')}
              </span>
            )}
            {currentView === 'three_day' && (
              <span className="text-sm font-normal px-2 py-0.5 bg-[#205799] text-white rounded-full">
                3 Days
              </span>
            )}
          </h2>

        </div>

        <div className="flex items-center gap-4">
          <div className="flex rounded-lg border border-gray-300 overflow-hidden">
            <button
              onClick={() => setCurrentView('month')}
              className={`px-4 py-2 text-sm font-medium ${
                currentView === 'month'
                  ? 'bg-gray-100 text-gray-900'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              Month
            </button>
            <button
              onClick={() => setCurrentView('week')}
              className={`px-4 py-2 text-sm font-medium border-l border-gray-300 ${
                currentView === 'week'
                  ? 'bg-gray-100 text-gray-900'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              Week
            </button>
            <button
              onClick={() => setCurrentView('day')}
              className={`px-4 py-2 text-sm font-medium border-l border-gray-300 ${
                currentView === 'day'
                  ? 'bg-gray-100 text-gray-900'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              Day
            </button>
          </div>
          {/* Refresh Button */}
          <button
            onClick={onRefresh}
            disabled={isRefreshing}
            title="Refresh timelogs"
            className="p-2 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50"
          >
            <svg className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>

          {/* Dropdown Button */}
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setShowDropdown(!showDropdown)}
              className="px-4 py-2 text-sm font-bold bg-[#205799] text-white rounded-lg flex items-center gap-2"
            >
              Add 
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            
            {/* Dropdown Menu */}
            {showDropdown && (
              <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                <div className="py-1">
                  <button
                    onClick={() => {
                      onAddNew();
                      setShowDropdown(false);
                    }}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    New entry
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
} 